import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

export const buttonVariants = cva(
  [
    "group/btn relative inline-flex items-center justify-center gap-2 whitespace-nowrap",
    // cápsula: botão, chip, busca e segmented são pílula inteira (DESIGN.md › Shapes)
    "rounded-pill border font-medium select-none",
    "transition-[background-color,border-color,color,box-shadow] duration-[var(--duration-micro)]",
    // pressionar = empurrão de meio pixel, nunca escala/bounce
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- empurrão de 0,5px no clique é especificação de movimento (DESIGN_SYSTEM §9), abaixo da escala
    "active:enabled:translate-y-[0.5px]",
    // foco: anel de 3px violeta
    "focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/40",
    "disabled:cursor-not-allowed disabled:opacity-45",
    // alvo de toque de 44px no mobile sem mudar a altura visual no desktop
    "max-md:min-h-11",
  ],
  {
    variants: {
      variant: {
        /** CTA da tela, violeta. Um por tela (DESIGN_SYSTEM seção 1.5). */
        primary:
          "border-transparent bg-primary text-primary-foreground hover:enabled:bg-primary-hover",
        secondary:
          "border-border bg-surface text-foreground hover:enabled:border-border-strong hover:enabled:bg-surface-subtle",
        /** Confirmar/aplicar em tinta escura (filtros, "Aplicar"). */
        dark: "border-transparent bg-surface-inverse text-inverse-foreground hover:enabled:opacity-90",
        /** Ação de marca secundária ("Salvar e concluir"). */
        soft: "border-violet-100 bg-primary-soft text-primary-text hover:enabled:bg-violet-100",
        /** Cancelar/limpar. */
        neutral: "border-transparent bg-muted text-foreground hover:enabled:bg-border",
        ghost:
          "border-transparent bg-transparent text-foreground hover:enabled:bg-muted",
        /** Ação destrutiva. Exige confirmação em Dialog. */
        danger:
          "border-transparent bg-danger text-danger-foreground hover:enabled:opacity-90",
        /** Destrutiva de baixo peso (zona de perigo das configurações). */
        "danger-soft":
          "border-transparent bg-danger-soft text-danger hover:enabled:bg-red-100",
        /** Contorno violeta (botão de filtros com contador). */
        "outline-brand":
          "border-primary bg-surface text-primary-text hover:enabled:bg-primary-soft",
        /**
         * Atenção imediata (seção 8: "Reconectar" em QR_REQUIRED). Desde out/2026
         * a cor de ação é uma só, o violeta — `accent` é o mesmo visual de
         * `primary` e continua ocupando a MESMA vaga de "única cor de ação".
         */
        accent:
          "border-transparent bg-accent text-accent-foreground hover:enabled:bg-primary-hover",
      },
      size: {
        sm: "h-7 px-3 text-xs gap-1.5",
        md: "h-9 px-4 text-sm",
        lg: "h-11 px-5 text-base",
      },
      /** Largura cheia — padrão de ação importante no mobile. */
      block: {
        true: "w-full",
        false: "",
      },
      /** Ícone inicial dentro de um círculo (padrão "+ Adicionar"): encolhe o recuo. */
      bubble: { true: "", false: "" },
    },
    compoundVariants: [
      { bubble: true, size: "sm", className: "pl-0.5" },
      { bubble: true, size: "md", className: "pl-1" },
      { bubble: true, size: "lg", className: "pl-1.5" },
    ],
    defaultVariants: {
      // secundário por padrão: `primary` tem que ser uma escolha consciente,
      // senão a tela vira um tabuleiro de CTAs.
      variant: "secondary",
      size: "md",
      block: false,
      bubble: false,
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

const BUBBLE_SIZE: Record<ButtonSize, string> = {
  sm: "size-5.5",
  md: "size-7",
  lg: "size-8",
};

/** Fundo do círculo do ícone por variante (no primary é branco com ícone violeta). */
const BUBBLE_TONE: Partial<Record<ButtonVariant, string>> = {
  primary: "bg-primary-foreground text-primary",
  accent: "bg-primary-foreground text-primary",
  secondary: "bg-muted",
  dark: "bg-inverse-foreground/15",
  danger: "bg-danger-foreground/20",
};

const COUNT_TONE: Partial<Record<ButtonVariant, string>> = {
  "outline-brand": "bg-violet-100 text-violet-600",
  soft: "bg-violet-100 text-violet-600",
  secondary: "bg-muted text-foreground",
  neutral: "bg-surface text-foreground",
  ghost: "bg-muted text-foreground",
  "danger-soft": "bg-red-100 text-danger",
};

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, "bubble"> {
  /**
   * Em carregamento: a largura NÃO muda e o rótulo não some do DOM
   * (acessibilidade) — ele fica invisível e um skeleton ocupa o lugar.
   * Nada de spinner girando: seção 1.6 do DESIGN_SYSTEM.
   */
  loading?: boolean;
  /** Texto lido por leitor de tela enquanto carrega. */
  loadingLabel?: string;
  /** Ícone antes do rótulo. Se o botão for SÓ ícone, passe `aria-label` (ou use IconButton). */
  leadingIcon?: ReactNode;
  /** Põe o `leadingIcon` dentro de um círculo (padrão "+ Adicionar"). */
  leadingBubble?: boolean;
  /** Ícone depois do rótulo (chevron, link externo). */
  trailingIcon?: ReactNode;
  /** Contador dentro do botão (ex.: Filtros 2). */
  count?: number;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    loading = false,
    loadingLabel = "Carregando",
    leadingIcon,
    leadingBubble = false,
    trailingIcon,
    count,
    disabled,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  const v: ButtonVariant = variant ?? "secondary";
  const s: ButtonSize = size ?? "md";
  const bubble = Boolean(leadingIcon) && leadingBubble;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      className={cn(buttonVariants({ variant, size, block, bubble }), className)}
      {...props}
    >
      <span
        className={cn(
          "inline-flex items-center gap-[inherit] [&_svg]:size-4",
          s === "sm" && "[&_svg]:size-3.5",
          loading && "invisible",
        )}
      >
        {bubble ? (
          <span
            aria-hidden="true"
            className={cn(
              "inline-grid shrink-0 place-items-center rounded-full",
              BUBBLE_SIZE[s],
              BUBBLE_TONE[v] ?? "bg-muted",
            )}
          >
            {leadingIcon}
          </span>
        ) : (
          leadingIcon
        )}
        {children}
        {count !== undefined ? (
          <span
            className={cn(
              "nums inline-grid h-4.5 min-w-4.5 place-items-center rounded-pill px-1.5 text-2xs font-semibold leading-none",
              COUNT_TONE[v] ?? "bg-primary-foreground/20",
            )}
          >
            {count}
          </span>
        ) : null}
        {trailingIcon}
      </span>

      {loading ? (
        <>
          <span
            aria-hidden="true"
            className="absolute inset-x-3.5 h-2 animate-pulse rounded-xs bg-current opacity-35 [animation-duration:1.5s]"
          />
          <span className="sr-only">{loadingLabel}</span>
        </>
      ) : null}
    </button>
  );
});

/* ------------------------------------------------------------------------ */

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "children"> {
  /** Ícone (nó Lucide ou `<Icon name=…>`). */
  icon: ReactNode;
  /** Obrigatório: botão só-ícone sem nome é invisível para leitor de tela. Vira aria-label e title. */
  label: string;
  size?: "sm" | "md";
  /** outline = topbar (ajuda, notificações); ghost = ação de linha de tabela. */
  variant?: "outline" | "ghost";
  /** Ponto vermelho de notificação. */
  dot?: boolean;
}

/** Botão redondo só-ícone (ajuda, notificações, ações de linha). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = "md", variant = "outline", dot = false, className, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "relative inline-grid shrink-0 place-items-center rounded-full border text-foreground",
        "transition-colors duration-[var(--duration-micro)]",
        // oxlint-disable-next-line eslint-js/no-restricted-syntax -- empurrão de 0,5px no clique é especificação de movimento (DESIGN_SYSTEM §9), abaixo da escala
        "active:enabled:translate-y-[0.5px]",
        "focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/40",
        "disabled:cursor-not-allowed disabled:opacity-45",
        // alvo de toque de 44px sem inchar o visual
        "hit-44",
        size === "md" ? "size-9 [&_svg]:size-4" : "size-7 [&_svg]:size-3.5",
        variant === "outline"
          ? "border-border bg-surface hover:enabled:bg-surface-subtle"
          : "border-transparent bg-transparent hover:enabled:bg-muted",
        className,
      )}
      {...props}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
      {dot ? (
        <span
          aria-hidden="true"
          className="absolute right-2 top-1.5 size-1.75 rounded-full bg-red-500 ring-2 ring-surface"
        />
      ) : null}
    </button>
  );
});
